import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { BarChart3, Download, Play, Plus, X, FileSpreadsheet, Save, Bookmark, Trash2 } from "lucide-react";

const AMOUNT_MODES = [
  { value: "split_columns", label: "Separate Debit & Credit columns" },
  { value: "signed",        label: "Single amount column (- = Debit, + = Credit)" },
  { value: "typed",         label: "Amount column + D/C flag column" },
];

const fmtNum = (n) => (typeof n === "number"
  ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : n);
const fmtPct = (n) => (typeof n === "number" ? `${n.toFixed(2)}%` : "—");

function RunPicker({ value, onChange }) {
  const [runs, setRuns] = useState([]);
  const [mruns, setMruns] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/reconciliation/runs").then(r => r.data?.runs || r.data || []).catch(() => []),
      api.get("/multi-runs").then(r => r.data || []).catch(() => []),
    ]).then(([a, b]) => { setRuns(a); setMruns(b); });
  }, []);
  return (
    <select
      data-testid="report-run-select"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "0.55rem 0.75rem", border: "1px solid var(--border-default)", borderRadius: "0.4rem", fontSize: "0.85rem", background: "white" }}
    >
      <option value="">— pick a run —</option>
      <optgroup label="2-file runs">
        {runs.map(r => (
          <option key={`r_${r.id}`} value={`r:${r.id}`}>
            {r.name} · {new Date(r.created_at).toLocaleDateString()}
          </option>
        ))}
      </optgroup>
      <optgroup label="Multi-file runs">
        {mruns.map(m => (
          <option key={`m_${m.id}`} value={`m:${m.id}`}>
            {m.name} · {new Date(m.created_at).toLocaleDateString()}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

export default function ReportBuilderPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  // Preselect run from query string (?run=… | ?multi=…)
  const initialSel = sp.get("run") ? `r:${sp.get("run")}` : (sp.get("multi") ? `m:${sp.get("multi")}` : "");
  const [selection, setSelection] = useState(initialSel);  const [side, setSide] = useState("a");
  const [mode, setMode] = useState("split_columns");
  const [cfg, setCfg] = useState({
    debit_col: "",
    credit_col: "",
    amount_col: "",
    dr_cr_col: "",
    dr_value: "D",
    cr_value: "C",
  });
  const [groupBy, setGroupBy] = useState([""]);
  const [availableCols, setAvailableCols] = useState([]);
  const [legs, setLegs] = useState([]);
  const [scope, setScope] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tolSummary, setTolSummary] = useState(null);
  const [tolLoading, setTolLoading] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newTplName, setNewTplName] = useState("");
  const refreshTemplates = useCallback(() => {
    api.get("/reports/templates").then(r => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, []);
  useEffect(() => { refreshTemplates(); }, [refreshTemplates]);

  const applyTemplate = (t) => {
    const c = t.config || {};
    setSide(c.side || "a");
    setMode(c.amount_mode || "split_columns");
    setCfg({
      debit_col: c.debit_col || "", credit_col: c.credit_col || "",
      amount_col: c.amount_col || "",
      dr_cr_col: c.dr_cr_col || "", dr_value: c.dr_value || "D", cr_value: c.cr_value || "C",
    });
    setGroupBy(c.group_by && c.group_by.length ? c.group_by : [""]);
    setReport(null);
  };

  const saveTemplate = async () => {
    if (!newTplName.trim()) return;
    const e = validate();
    if (e) { setErr(e); return; }
    try {
      await api.post("/reports/templates", { name: newTplName.trim(), config: buildBody() });
      setSaveOpen(false); setNewTplName("");
      refreshTemplates();
    } catch (x) {
      setErr(`Save template failed: ${x.response?.data?.detail || x.message}`);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/reports/templates/${tpl.id}`);
      refreshTemplates();
    } catch (x) {
      setErr(`Delete failed: ${x.response?.data?.detail || x.message}`);
    }
  };

  const isMulti = selection.startsWith("m:");
  const runId = selection.startsWith("r:") ? selection.slice(2) : null;
  const mrId = selection.startsWith("m:") ? selection.slice(2) : null;

  // Load columns for the chosen run
  useEffect(() => {
    setAvailableCols([]); setLegs([]); setReport(null); setErr(""); setTolSummary(null);
    if (!selection) return;
    if (runId) {
      api.get(`/reports/run-columns/${runId}`, { params: { side } })
        .then(r => setAvailableCols(r.data.columns || []))
        .catch(() => setAvailableCols([]));
    } else if (mrId) {
      api.get(`/reports/multi-run-columns/${mrId}`, { params: { side } })
        .then(r => { setAvailableCols(r.data.columns || []); setLegs(r.data.legs || []); })
        .catch(() => setAvailableCols([]));
    }
  }, [selection, runId, mrId, side]);

  const colOpts = useMemo(() => availableCols.map(c => <option key={c} value={c}>{c}</option>), [availableCols]);

  const buildBody = useCallback(() => {
    const body = {
      side,
      amount_mode: mode,
      debit_col: cfg.debit_col || null,
      credit_col: cfg.credit_col || null,
      amount_col: cfg.amount_col || null,
      dr_cr_col: cfg.dr_cr_col || null,
      dr_value: cfg.dr_value || "D",
      cr_value: cfg.cr_value || "C",
      group_by: (groupBy || []).filter(Boolean),
    };
    if (runId) body.run_id = runId;
    if (mrId)  { body.multi_run_id = mrId; body.scope = scope; }
    return body;
  }, [runId, mrId, scope, side, mode, cfg, groupBy]);

  const validate = () => {
    if (!selection) return "Pick a run to report on.";
    if (mode === "split_columns" && !cfg.debit_col && !cfg.credit_col) return "Pick at least a Debit or a Credit column.";
    if (mode === "signed" && !cfg.amount_col) return "Pick the signed amount column.";
    if (mode === "typed"  && (!cfg.amount_col || !cfg.dr_cr_col)) return "Pick the amount column AND the D/C flag column.";
    return "";
  };

  const runReport = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setErr("");
    setLoading(true);
    try {
      const r = await api.post("/reports/generate", buildBody());
      setReport(r.data);
    } catch (x) {
      setErr(x.response?.data?.detail || x.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTolSummary = async () => {
    if (!runId) return;
    setTolLoading(true);
    try {
      const r = await api.get(`/reports/recon/${runId}/tolerance-summary`);
      setTolSummary(r.data);
    } catch (x) {
      setErr(`Tolerance summary failed: ${x.response?.data?.detail || x.message}`);
    } finally {
      setTolLoading(false);
    }
  };

  const download = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setErr("");
    try {
      const r = await api.post("/reports/export-xlsx", buildBody(), { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(report?.label || "report").replace(/[^\w\- ]+/g, "_")}_report.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (x) {
      setErr(`Download failed: ${x.message}`);
    }
  };

  return (
    <Layout>
      <div style={{ padding: "1.25rem 1.75rem", maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
          <BarChart3 size={20} />
          <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.6rem", margin: 0 }}>Reports</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0, marginLeft: "0.5rem" }}>
            Slice total / matched / unmatched amounts by any column. Export as Excel.
          </p>
        </div>

        {/* Templates bar */}
        <div className="card" data-testid="templates-bar" style={{ padding: "0.6rem 0.875rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <Bookmark size={14} style={{ color: "var(--text-muted)" }} />
          <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-secondary)" }}>Saved templates:</span>
          {templates.length === 0 ? (
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>— none yet —</span>
          ) : (
            templates.map(t => (
              <span key={t.id} data-testid={`tpl-${t.id}`} style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", background: "var(--sage-light)", color: "var(--sage)", border: "1px solid var(--sage-border)", borderRadius: "9999px", padding: "0.15rem 0.6rem", fontSize: "0.75rem" }}>
                <button onClick={() => applyTemplate(t)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 600, padding: 0 }}>
                  {t.name}
                </button>
                <button onClick={() => deleteTemplate(t)} title="Delete template"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, display: "flex" }}>
                  <Trash2 size={11} />
                </button>
              </span>
            ))
          )}
          <div style={{ marginLeft: "auto", display: "inline-flex", gap: "0.4rem" }}>
            {!saveOpen ? (
              <button data-testid="save-template-btn" onClick={() => setSaveOpen(true)} className="btn-secondary"
                style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                <Save size={11} /> Save current as template
              </button>
            ) : (
              <div style={{ display: "inline-flex", gap: "0.3rem", alignItems: "center" }}>
                <input data-testid="tpl-name-input" value={newTplName} onChange={e => setNewTplName(e.target.value)}
                  placeholder="Template name"
                  style={{ fontSize: "0.76rem", padding: "0.25rem 0.5rem", border: "1px solid var(--border-default)", borderRadius: "0.35rem", width: "160px" }} />
                <button data-testid="tpl-save-confirm" onClick={saveTemplate} className="btn-primary"
                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}>Save</button>
                <button onClick={() => { setSaveOpen(false); setNewTplName(""); }} className="btn-secondary"
                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}>Cancel</button>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          {/* ── Configure ─────────────────────────────────────────────────── */}
          <div className="card" style={{ padding: "1rem" }}>
            <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: "0 0 0.75rem" }}>1. Pick a run</h3>
            <RunPicker value={selection} onChange={setSelection} />

            {isMulti && legs.length > 0 && (
              <div style={{ marginTop: "0.75rem" }}>
                <label style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Scope (multi-file)</label>
                <select data-testid="report-scope-select" value={scope} onChange={e => setScope(e.target.value)}
                  style={{ width: "100%", padding: "0.45rem 0.6rem", marginTop: "0.2rem", border: "1px solid var(--border-default)", borderRadius: "0.35rem", fontSize: "0.82rem", background: "white" }}>
                  <option value="all">All legs (merged)</option>
                  {legs.map(l => <option key={l.leg_run_id} value={l.leg_run_id}>{l.target_file_name}</option>)}
                </select>
              </div>
            )}

            <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: "1.25rem 0 0.5rem" }}>2. Which side?</h3>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {["a", "b"].map(s => (
                <button key={s} data-testid={`side-${s}`}
                  onClick={() => setSide(s)}
                  className={side === s ? "btn-primary" : "btn-secondary"}
                  style={{ fontSize: "0.78rem", padding: "0.4rem 1rem" }}>
                  {s === "a" ? "File A" : "File B"}
                </button>
              ))}
            </div>

            <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: "1.25rem 0 0.5rem" }}>3. How is the amount encoded?</h3>
            <select data-testid="report-mode" value={mode} onChange={e => setMode(e.target.value)}
              style={{ width: "100%", padding: "0.45rem 0.6rem", border: "1px solid var(--border-default)", borderRadius: "0.35rem", fontSize: "0.82rem", background: "white" }}>
              {AMOUNT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>

            <div style={{ marginTop: "0.6rem", display: "grid", gap: "0.5rem" }}>
              {mode === "split_columns" && (
                <>
                  <div>
                    <Label>Debit column</Label>
                    <Select testid="report-debit-col" value={cfg.debit_col} onChange={v => setCfg(c => ({ ...c, debit_col: v }))} options={colOpts} />
                  </div>
                  <div>
                    <Label>Credit column</Label>
                    <Select testid="report-credit-col" value={cfg.credit_col} onChange={v => setCfg(c => ({ ...c, credit_col: v }))} options={colOpts} />
                  </div>
                </>
              )}
              {mode === "signed" && (
                <div>
                  <Label>Amount column</Label>
                  <Select testid="report-amount-col" value={cfg.amount_col} onChange={v => setCfg(c => ({ ...c, amount_col: v }))} options={colOpts} />
                </div>
              )}
              {mode === "typed" && (
                <>
                  <div>
                    <Label>Amount column</Label>
                    <Select testid="report-amount-col" value={cfg.amount_col} onChange={v => setCfg(c => ({ ...c, amount_col: v }))} options={colOpts} />
                  </div>
                  <div>
                    <Label>D/C flag column</Label>
                    <Select testid="report-drcr-col" value={cfg.dr_cr_col} onChange={v => setCfg(c => ({ ...c, dr_cr_col: v }))} options={colOpts} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                    <div>
                      <Label>Value = Debit</Label>
                      <input data-testid="report-dr-value" value={cfg.dr_value} onChange={e => setCfg(c => ({ ...c, dr_value: e.target.value }))} className="recon-input" />
                    </div>
                    <div>
                      <Label>Value = Credit</Label>
                      <input data-testid="report-cr-value" value={cfg.cr_value} onChange={e => setCfg(c => ({ ...c, cr_value: e.target.value }))} className="recon-input" />
                    </div>
                  </div>
                </>
              )}
            </div>

            <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: "1.25rem 0 0.5rem" }}>4. Slice by (bucket columns)</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {groupBy.map((g, i) => (
                <div key={i} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                  <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", width: "1.2rem", textAlign: "right" }}>{i + 1}</span>
                  <Select testid={`report-group-${i}`} value={g} onChange={v => { const n = [...groupBy]; n[i] = v; setGroupBy(n); }} options={colOpts} placeholder="— pick column —" />
                  {groupBy.length > 1 && (
                    <button onClick={() => setGroupBy(groupBy.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
              <button data-testid="add-group" className="btn-secondary" onClick={() => setGroupBy([...groupBy, ""])}
                style={{ alignSelf: "flex-start", fontSize: "0.75rem", padding: "0.25rem 0.55rem" }}>
                <Plus size={11} style={{ marginRight: 4 }} /> Add column
              </button>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
              <button data-testid="run-report-btn" onClick={runReport} disabled={loading} className="btn-primary"
                style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", padding: "0.5rem 1rem" }}>
                <Play size={13} /> {loading ? "Running…" : "Run report"}
              </button>
              <button data-testid="export-xlsx-btn" onClick={download} disabled={!report} className="btn-secondary"
                style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", padding: "0.5rem 1rem" }}>
                <FileSpreadsheet size={13} /> Export Excel
              </button>
            </div>
            {err && <div style={{ marginTop: "0.75rem", padding: "0.55rem 0.75rem", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.35rem", color: "#991B1B", fontSize: "0.78rem" }}>{err}</div>}
          </div>

          {/* ── Result ───────────────────────────────────────────────────── */}
          <div className="card" style={{ padding: "1rem" }}>
            <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: "0 0 0.75rem" }}>Summary</h3>
            {!report ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem" }}>Configure and run the report to see results here.</div>
            ) : (
              <SummaryTable summary={report.summary} />
            )}
          </div>
        </div>

        {report?.buckets?.length > 0 && (
          <div className="card" style={{ padding: "1rem", marginTop: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: 0 }}>
                Buckets · grouped by {(report.group_by || []).join(" / ")} · {report.buckets.length} rows
              </h3>
            </div>
            <BucketsTable groupBy={report.group_by} buckets={report.buckets} />
          </div>
        )}

        {report?.by_rule?.length > 0 && (
          <div className="card" style={{ padding: "1rem", marginTop: "1rem" }} data-testid="by-rule-card">
            <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: "0 0 0.5rem" }}>
              Matched · Per Rule contribution
            </h3>
            <ByRuleTable rows={report.by_rule} />
          </div>
        )}

        {runId && (
          <div className="card" style={{ padding: "1rem", marginTop: "1rem" }} data-testid="tol-summary-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.6rem" }}>
              <div>
                <h3 style={{ fontSize: "0.92rem", fontWeight: 600, margin: 0 }}>Tolerance Match Distribution</h3>
                <p style={{ margin: "0.15rem 0 0", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                  How close to exact were your tolerance matches? Lower bucket = tighter match.
                </p>
              </div>
              <button data-testid="tol-summary-load"
                onClick={loadTolSummary} disabled={tolLoading}
                className="btn-secondary" style={{ fontSize: "0.8rem", padding: "0.4rem 0.85rem" }}>
                {tolLoading ? "Loading…" : tolSummary ? "Refresh" : "Load tolerance summary"}
              </button>
            </div>
            {tolSummary && <TolSummaryView summary={tolSummary} />}
          </div>
        )}
      </div>
    </Layout>
  );
}

function TolSummaryView({ summary }) {
  const order = summary.buckets_order || [];
  const totals = summary.totals || {};
  const total = summary.total_deltas || 0;
  if (!total) {
    return <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--text-muted)" }}>No tolerance-matched rows in this run. Either there are no numeric-tolerance rules, or all matches were exact.</p>;
  }
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${order.length}, 1fr)`, gap: "0.35rem", marginBottom: "0.8rem" }}>
        {order.map(b => {
          const v = totals[b] || 0;
          const pct = total ? (v / total) * 100 : 0;
          return (
            <div key={b} data-testid={`tol-bucket-${b}`} style={{ padding: "0.55rem 0.5rem", background: "var(--sage-light)", borderRadius: 8, border: "1px solid var(--sage-border)", textAlign: "center" }}>
              <div style={{ fontSize: "0.65rem", color: "var(--sage)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{b}</div>
              <div style={{ fontSize: "1.1rem", fontWeight: 700, fontFamily: "JetBrains Mono, monospace", marginTop: "0.15rem" }}>{v}</div>
              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{pct.toFixed(1)}%</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: "1rem", marginBottom: "0.8rem", fontSize: "0.78rem", color: "var(--text-secondary)" }}>
        <span><strong>Total deltas:</strong> {total}</span>
        <span><strong>Max delta:</strong> {summary.max_delta}</span>
        <span><strong>Avg delta:</strong> {summary.avg_delta}</span>
      </div>
      {summary.by_column_pair?.length > 0 && (
        <div style={{ marginTop: "0.8rem" }}>
          <h4 style={{ fontSize: "0.78rem", margin: "0 0 0.3rem", fontWeight: 600 }}>Per column pair</h4>
          <TolBucketsTable rows={summary.by_column_pair} order={order} />
        </div>
      )}
      {summary.by_rule?.length > 0 && (
        <div style={{ marginTop: "0.8rem" }}>
          <h4 style={{ fontSize: "0.78rem", margin: "0 0 0.3rem", fontWeight: 600 }}>Per rule</h4>
          <TolBucketsTable rows={summary.by_rule} order={order} />
        </div>
      )}
    </div>
  );
}

function TolBucketsTable({ rows, order }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
        <thead>
          <tr>
            <th className="table-header" style={{ textAlign: "left" }}>Name</th>
            {order.map(b => <th key={b} className="table-header" style={{ textAlign: "right" }}>{b}</th>)}
            <th className="table-header" style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="table-row">
              <td className="table-cell" style={{ fontWeight: 500 }}>{r.name}</td>
              {order.map(b => <td key={b} className="table-cell" style={{ textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{r[b] || 0}</td>)}
              <td className="table-cell" style={{ textAlign: "right", fontWeight: 600 }}>{r.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Label({ children }) {
  return <label style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.2rem" }}>{children}</label>;
}

function Select({ value, onChange, options, placeholder = "— pick —", testid }) {
  return (
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "0.4rem 0.55rem", border: "1px solid var(--border-default)", borderRadius: "0.35rem", fontSize: "0.8rem", background: "white", color: value ? "var(--text-primary)" : "var(--text-muted)" }}>
      <option value="">{placeholder}</option>
      {options}
    </select>
  );
}

function SummaryTable({ summary }) {
  const row = (lbl, d, c, n, bold) => (
    <tr>
      <td className="table-cell" style={{ fontSize: "0.8rem", fontWeight: bold ? 600 : 400 }}>{lbl}</td>
      <td className="table-cell" style={{ fontSize: "0.8rem", textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(d)}</td>
      <td className="table-cell" style={{ fontSize: "0.8rem", textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(c)}</td>
      <td className="table-cell" style={{ fontSize: "0.8rem", textAlign: "right" }}>{n}</td>
    </tr>
  );
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="report-summary-table">
      <thead>
        <tr>
          <th className="table-header">Metric</th>
          <th className="table-header" style={{ textAlign: "right" }}>Debit</th>
          <th className="table-header" style={{ textAlign: "right" }}>Credit</th>
          <th className="table-header" style={{ textAlign: "right" }}>Rows</th>
        </tr>
      </thead>
      <tbody>
        {row("Total input",  summary.total_debit,     summary.total_credit,     summary.input_rows,     true)}
        {row("Matched",      summary.matched_debit,   summary.matched_credit,   summary.matched_rows)}
        {row("Unmatched",    summary.unmatched_debit, summary.unmatched_credit, summary.unmatched_rows)}
        <tr>
          <td className="table-cell" style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>Matched %</td>
          <td className="table-cell" style={{ fontSize: "0.78rem", color: "var(--sage)", textAlign: "right", fontWeight: 600 }}>{fmtPct(summary.matched_debit_pct)}</td>
          <td className="table-cell" style={{ fontSize: "0.78rem", color: "var(--sage)", textAlign: "right", fontWeight: 600 }}>{fmtPct(summary.matched_credit_pct)}</td>
          <td />
        </tr>
        <tr>
          <td className="table-cell" style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>Unmatched %</td>
          <td className="table-cell" style={{ fontSize: "0.78rem", color: "#991B1B", textAlign: "right", fontWeight: 600 }}>{fmtPct(summary.unmatched_debit_pct)}</td>
          <td className="table-cell" style={{ fontSize: "0.78rem", color: "#991B1B", textAlign: "right", fontWeight: 600 }}>{fmtPct(summary.unmatched_credit_pct)}</td>
          <td />
        </tr>
      </tbody>
    </table>
  );
}

function ByRuleTable({ rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }} data-testid="report-by-rule-table">
        <thead>
          <tr>
            <th className="table-header">Rule</th>
            <th className="table-header" style={{ textAlign: "right" }}>Matched Debit</th>
            <th className="table-header" style={{ textAlign: "right" }}>Matched Credit</th>
            <th className="table-header" style={{ textAlign: "right" }}>Matched Rows</th>
            <th className="table-header" style={{ textAlign: "right" }}>% of Mch Dr</th>
            <th className="table-header" style={{ textAlign: "right" }}>% of Mch Cr</th>
            <th className="table-header" style={{ textAlign: "right" }}>% of Mch Rows</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="table-row" data-testid={`by-rule-row-${i}`}>
              <td className="table-cell" style={{ fontWeight: 500 }}>{r.rule_name}</td>
              <td className="table-cell" style={{ textAlign: "right", color: "var(--sage)", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(r.matched_debit)}</td>
              <td className="table-cell" style={{ textAlign: "right", color: "var(--sage)", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(r.matched_credit)}</td>
              <td className="table-cell" style={{ textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{r.matched_rows}</td>
              <td className="table-cell" style={{ textAlign: "right" }}>{fmtPct(r.pct_of_matched_debit)}</td>
              <td className="table-cell" style={{ textAlign: "right" }}>{fmtPct(r.pct_of_matched_credit)}</td>
              <td className="table-cell" style={{ textAlign: "right" }}>{fmtPct(r.pct_of_matched_rows)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BucketsTable({ groupBy, buckets }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }} data-testid="report-buckets-table">
        <thead>
          <tr>
            {groupBy.map(g => <th key={g} className="table-header">{g}</th>)}
            <th className="table-header" style={{ textAlign: "right" }}>Total Debit</th>
            <th className="table-header" style={{ textAlign: "right" }}>Total Credit</th>
            <th className="table-header" style={{ textAlign: "right" }}>Matched Dr</th>
            <th className="table-header" style={{ textAlign: "right" }}>Unmatched Dr</th>
            <th className="table-header" style={{ textAlign: "right" }}>Matched Cr</th>
            <th className="table-header" style={{ textAlign: "right" }}>Unmatched Cr</th>
            <th className="table-header" style={{ textAlign: "right" }}>Mch Dr %</th>
            <th className="table-header" style={{ textAlign: "right" }}>Umch Dr %</th>
            <th className="table-header" style={{ textAlign: "right" }}>Rows</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b, i) => (
            <tr key={i} className="table-row">
              {b.key.map((v, j) => <td key={j} className="table-cell">{v}</td>)}
              <td className="table-cell" style={{ textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(b.total_debit)}</td>
              <td className="table-cell" style={{ textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(b.total_credit)}</td>
              <td className="table-cell" style={{ textAlign: "right", color: "var(--sage)", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(b.matched_debit)}</td>
              <td className="table-cell" style={{ textAlign: "right", color: "#991B1B", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(b.unmatched_debit)}</td>
              <td className="table-cell" style={{ textAlign: "right", color: "var(--sage)", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(b.matched_credit)}</td>
              <td className="table-cell" style={{ textAlign: "right", color: "#991B1B", fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(b.unmatched_credit)}</td>
              <td className="table-cell" style={{ textAlign: "right" }}>{fmtPct(b.matched_debit_pct)}</td>
              <td className="table-cell" style={{ textAlign: "right" }}>{fmtPct(b.unmatched_debit_pct)}</td>
              <td className="table-cell" style={{ textAlign: "right" }}>{b.input_rows}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
